import React, { useState } from 'react';
import { Button, Card, Row, Col, Statistic, Progress, Tag, Space, Drawer, Modal, message, Upload, Typography, Form } from 'antd';
import { EyeOutlined, ReloadOutlined, DeleteOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, InboxOutlined, UploadOutlined } from '@ant-design/icons';
import API from '../../../API';
import axios from '../../../API/config';
import './ProjectManagement.css';
import { useEffect } from 'react';
import Icon from "@ant-design/icons";
import { ReactComponent as ReportsSvg } from "../../../assets/icons/reports.svg";
import EmptyState from '../../components/EmptyState';
import { DownloadOutlined } from '@ant-design/icons';
import ActionBar from '../../components/ActionBar';
import CustomPagination from '../../components/CustomPagination';
import { useHistory } from 'react-router-dom';


const ProjectOnboarding = () => {
    const [onboardingRecords, setOnboardingRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    });
    const history = useHistory();

    const fetchOnboardingRecords = async (page = 1) => {
        try {
            setLoading(true);
            const response = await API.getProjectOnboardingRecords({ page });
            console.log("response in project onboarding", response);
            if (response.success) {
                setOnboardingRecords(response.data);
                setPagination(response.pagination);
            }
        } catch (error) {
            console.error('Error fetching onboarding records:', error);
            message.error('Failed to fetch onboarding records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOnboardingRecords();
    }, []);

    const handlePageChange = (page, pageSize) => {
        fetchOnboardingRecords(page);
    };

    const handleRetry = async (recordId) => {
        try {
            setLoading(true);
            await API.retryProjectOnboarding(recordId);
            message.success('Onboarding retry initiated successfully');
            fetchOnboardingRecords();
        } catch (error) {
            console.error('Error retrying onboarding:', error);
            message.error('Failed to retry onboarding');
        } finally {
            fetchOnboardingRecords();
            setLoading(false);
        }
    };


    const handleDownloadTemplate = async () => {
        try {
            const res = await API.downloadOnboardingTemplate();
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'project_onboarding_template.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error('Failed to download template');
        }
    };

    const handleDelete = async (recordId) => {
        try {
            setLoading(true);
            await API.deleteProjectOnboarding(recordId);
            message.success('Record deleted successfully');
            setDeleteModalVisible(false);
            setRecordToDelete(null);
            fetchOnboardingRecords();
        } catch (error) {
            console.error('Error deleting record:', error);
            message.error('Failed to delete record');
        } finally {
            setLoading(false);
        }
    };

    const showDeleteModal = (record) => {
        setRecordToDelete(record);
        setDeleteModalVisible(true);
    };

    const handleView = (record) => {
        setSelectedRecord(record);
        setDrawerVisible(true);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'pending':
                return <ClockCircleOutlined style={{ color: '#faad14' }} />;
            case 'failed':
                return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
            default:
                return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'processing';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleAddProject = () => {
        setUploadModalVisible(true);
    };


    const handleException = () => {
        history.push('/exceptions');
    };

    const handleFileSelect = (info) => {
        const file = info.file;

        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel' ||
            file.name.endsWith('.xlsx') ||
            file.name.endsWith('.xls');

        if (!isExcel) {
            message.error('Please select an Excel file (.xlsx or .xls)');
            return;
        }

        setSelectedFile(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            message.error('Please select a file to upload');
            return;
        }

        try {
            setUploadLoading(true);

            const formData = new FormData();
            formData.append('excel_file', selectedFile, selectedFile.name);

            const response = await axios.post('/project-onboarding/upload', formData, {
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 200 || response.status === 201) {
                message.success('File uploaded successfully!');
                setUploadModalVisible(false);
                setSelectedFile(null);
                fetchOnboardingRecords();
            }
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
            message.error(errorMessage);
        } finally {
            setUploadLoading(false);
            setUploadModalVisible(false);
            fetchOnboardingRecords();
        }
    };

    const handleUploadCancel = () => {
        setUploadModalVisible(false);
        setSelectedFile(null);
    };

    const iconStyle = {
        width: 24,
        height: 24,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "8px",
        fontWeight: "700",
        color: "white",
    };

    const ReportsIcon = () => (
        <Icon
            style={{ ...iconStyle }}
            component={ReportsSvg}
            className="navmenu-icon"
        />
    );


    return (
        <div className="project-management">
            <ActionBar actionButtonProp={{ title: 'Project Onboarding' }} />
            <div className="section-header">
                <div className="header-content">
                    <h1 className="prst-section-title">Project Onboarding Records</h1>
                    <p className="prst-section-sub-title">
                        Manage your project onboarding records, view processing status, and manage data imports
                    </p>
                </div>
                <div className="project-management-header-buttons">
                <Button type="primary" icon={<UploadOutlined />} onClick={handleAddProject}>
                    Add Project
                </Button>
                <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                    Download Template
                </Button>
                </div>
            </div>

            <div className="projects-section">
                {/* <Title level={5}>Onboarding Records</Title> */}

                <div className="projects-grid">
                    {loading ? (
                        Array.from({ length: 3 }).map((_, index) => (
                            <Card
                                key={`loading-${index}`}
                                className="project-card loading-card"
                                bodyStyle={{ padding: '20px' }}
                            >
                                <div className="project-card-content">
                                    <div className="project-info">
                                        <div className="record-header">
                                            <div className="loading-title"></div>
                                            <div className="loading-status"></div>
                                        </div>

                                        <div className="loading-progress"></div>

                                        <div className="loading-summary">
                                            <div className="loading-stat"></div>
                                            <div className="loading-stat"></div>
                                            <div className="loading-stat"></div>
                                        </div>

                                        <div className="loading-metadata">
                                            <div className="loading-meta"></div>
                                            <div className="loading-meta"></div>
                                            <div className="loading-meta"></div>
                                        </div>
                                    </div>

                                </div>
                            </Card>
                        ))
                    ) : onboardingRecords.length === 0 ? (
                        <EmptyState
                            message="There are no project onboarding records to display."
                            iconName="reports"
                        />
                    ) : (
                        onboardingRecords.map((record) => (
                            <Card
                                key={record.id}
                                className="project-card"
                                bodyStyle={{ padding: '20px' }}
                            >
                                <div className="project-card-content">
                                    <div className="project-info">
                                        <div className="record-header">
                                            <h3 className="project-title">{record.filename}</h3>
                                            <div className="status-section">
                                                {getStatusIcon(record.status)}
                                                <Tag color={getStatusColor(record.status)} className="status-tag">
                                                    {record.status.toUpperCase()}
                                                </Tag>
                                            </div>
                                        </div>

                                        <div className="progress-section-onboarding">
                                            <Progress
                                                percent={record.progress_percentage}
                                                size="small"
                                                status={record.status === 'failed' ? 'exception' : 'normal'}
                                            />
                                        </div>



                                        <div className="record-metadata-onboarding">
                                            <Space size="large">
                                                {
                                                    record.status === 'completed' ? (
                                                        <div className="metadata-item">
                                                            <CheckCircleOutlined className="metadata-icon" />
                                                            <span className="metadata-value-success">
                                                                Project onboarding completed successfully
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="metadata-item">
                                                            <ExclamationCircleOutlined className="metadata-icon" />
                                                            <span className="metadata-value">{record.error_message}</span>
                                                            <Button
                                                                style={{
                                                                    marginLeft: '10px',
                                                                    backgroundColor: 'transparent',
                                                                    borderColor: '#FF6600',
                                                                    border: 'none',
                                                                    color: '#FF6600',
                                                                    hover: {
                                                                        backgroundColor: '#FF6600',
                                                                        color: '#fff'
                                                                    }
                                                                }}
                                                                onClick={() => handleException()}
                                                            >
                                                                Fix Exception
                                                            </Button>
                                                        </div>
                                                    )
                                                }
                                            </Space>
                                        </div>
                                        <div className="project-actions">
                                            <Space size="small">
                                                <Button
                                                    type="primary"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => handleView(record)}
                                                    className="view-btn"
                                                    size="small"
                                                >
                                                    View
                                                </Button>

                                                {record.status === 'pending' || record.status === 'failed' && (
                                                    <Button
                                                        type="default"
                                                        icon={<ReloadOutlined />}
                                                        onClick={() => handleRetry(record.id)}
                                                        className="retry-btn"
                                                        size="small"
                                                        loading={loading}
                                                    >
                                                        Retry
                                                    </Button>
                                                )}

                                                <Button
                                                    type="default"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => showDeleteModal(record)}
                                                    className="delete-btn"
                                                    size="small"
                                                >
                                                    Delete
                                                </Button>
                                            </Space>
                                        </div>
                                    </div>


                                </div>
                            </Card>
                        ))
                    )}
                </div>
                
                <CustomPagination
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    loading={loading}
                />
            </div>

            <Drawer
                title="Onboarding Record Details"
                placement="right"
                width={450}
                onClose={() => setDrawerVisible(false)}
                visible={drawerVisible}
            >
                {selectedRecord && (
                    <div className="record-details">
                        <h3>File: {selectedRecord.filename}</h3>
                        <p><strong>Status:</strong> {selectedRecord.status}</p>
                        <p><strong>Progress:</strong> {selectedRecord.progress_percentage}%</p>

                        <h4>Processing Summary</h4>
                        <div className="processing-summary">
                            <Row gutter={[16, 8]}>
                                <Col span={8}>
                                    <Statistic
                                        title="Projects"
                                        value={selectedRecord.processing_summary?.projects?.processed || 0}
                                        suffix={`/ ${(selectedRecord.processing_summary?.projects?.processed || 0) + (selectedRecord.processing_summary?.projects?.errors || 0)}`}
                                        valueStyle={{ fontSize: '14px' }}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Packages"
                                        value={selectedRecord.processing_summary?.packages?.processed || 0}
                                        suffix={`/ ${(selectedRecord.processing_summary?.packages?.processed || 0) + (selectedRecord.processing_summary?.packages?.errors || 0)}`}
                                        valueStyle={{ fontSize: '14px' }}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Sub Projects"
                                        value={selectedRecord.processing_summary?.sub_projects?.processed || 0}
                                        suffix={`/ ${(selectedRecord.processing_summary?.sub_projects?.processed || 0) + (selectedRecord.processing_summary?.sub_projects?.errors || 0)}`}
                                        valueStyle={{ fontSize: '14px' }}
                                    />
                                </Col>
                            </Row>
                        </div>

                        <h4>Timestamps</h4>
                        <p><strong>Started:</strong> {formatDate(selectedRecord.started_at)}</p>
                        <p><strong>Completed:</strong> {formatDate(selectedRecord.completed_at)}</p>
                        <p><strong>Created:</strong> {formatDate(selectedRecord.created_at)}</p>

                        {selectedRecord.error_message && (
                            <div>
                                <h4>Error Message</h4>
                                <p className="error-message">{selectedRecord.error_message}</p>
                            </div>
                        )}
                    </div>
                )}
            </Drawer>

            <Modal
                title="Confirm Delete"
                visible={deleteModalVisible}
                onOk={() => handleDelete(recordToDelete?.id)}
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setRecordToDelete(null);
                }}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                confirmLoading={loading}
            >
                <p>Are you sure you want to delete the onboarding record "{recordToDelete?.filename}"?</p>
                <p>This action cannot be undone.</p>
            </Modal>

            <Modal
                title="Upload Project Onboarding File"
                visible={uploadModalVisible}
                onOk={handleUpload}
                onCancel={handleUploadCancel}
                okText="Upload"
                cancelText="Cancel"
                okButtonProps={{
                    type: 'primary',
                    disabled: !selectedFile,
                    loading: uploadLoading
                }}
                width={500}
                className="upload-modal"
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Upload.Dragger
                        name="excel_file"
                        multiple={false}
                        accept=".xlsx,.xls"
                        beforeUpload={() => false}
                        onChange={handleFileSelect}
                        showUploadList={false}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text">
                            {selectedFile ? selectedFile.name : 'Click or drag Excel file to this area to upload'}
                        </p>
                        <p className="ant-upload-hint">
                            {selectedFile ? 'File selected successfully!' : 'Only Excel files (.xlsx, .xls) are allowed'}
                        </p>
                    </Upload.Dragger>

                    {selectedFile && (
                        <div className="file-selected-info">
                            <p>
                                <strong>Selected File:</strong> {selectedFile.name}
                            </p>
                            <p className="file-size">
                                Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ProjectOnboarding;
